import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';

// icons
import CommentIcon from '@mui/icons-material/Comment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// constants
/* import {ERROR_MESSAGES} from '../constants/reservation';
 */
// ----------------------------------------------------------------------

const OCCASIONS = [
  '',
  'Anniversaire',
  'Repas d\'affaires',
  'Mariage',
  'Fiançailles',
  'Anniversaire de mariage',
  'Fête de famille',
  'Autre',
];

// ----------------------------------------------------------------------

// Conteneur pour la page de détails avec hauteur adaptative
const DetailsContainer = styled(Box)(({theme}) => ({
  maxWidth: '100%',
  padding: theme.spacing(2, 0, 3),
}));

// Style pour éviter la propagation des clics
const NoEventPropagationBox = styled(Box)(() => ({
  width: '100%',
}));

// Custom select avec OutlinedInput + Popover
const CustomSelect = styled(OutlinedInput)(({theme}) => ({
  'cursor': 'pointer',
  '& .MuiOutlinedInput-notchedOutline': {
    transition: theme.transitions.create(['box-shadow', 'border-color']),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

// Style pour le menu déroulant personnalisé
const CustomPopover = styled(Popover)(({theme}) => ({
  '& .MuiPaper-root': {
    marginTop: theme.spacing(1),
    boxShadow: theme.shadows[8],
    maxHeight: 250,
  },
}));

const CustomListItem = styled(ListItem)(({theme, isSelected}) => ({
  'cursor': 'pointer',
  'transition': theme.transitions.create(['background-color']),
  'padding': theme.spacing(1, 2),
  ...(isSelected && {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
}));

// Champ de texte pour les demandes spéciales
const SpecialRequestsInput = styled(OutlinedInput)(({theme}) => ({
  'cursor': 'text',
  'width': '100%',
  'marginBottom': theme.spacing(3),
  '& .MuiOutlinedInput-notchedOutline': {
    transition: theme.transitions.create(['box-shadow', 'border-color']),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

// ----------------------------------------------------------------------

export function AdditionalDetailsStep({
  formData,
  updateFormData,
  config,
  setValidation,
  setErrors,
  errors,
  setStepReady, // Add this new prop
}) {
  // État et refs pour le menu déroulant personnalisé
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const inputRef = useRef(null);

  // Fonction de validation - Les détails supplémentaires sont optionnels
  const validateStep = () => {
    // Pas de validation nécessaire pour cette étape, toujours valide
    return true;
  };

  // Enregistrer la fonction de validation auprès du parent
  useEffect(() => {
    setValidation(validateStep);
  }, [setValidation]);

  // Set step ready to true immediately since all fields are optional
  useEffect(() => {
    if (setStepReady) {
      // This enables the submit button as soon as the user reaches this step
      setStepReady(true);
    }
  }, [setStepReady]);

  // Ouvrir le menu déroulant personnalisé
  const handleOpenMenu = (event) => {
    // Empêcher la propagation pour éviter la fermeture
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    // Stocker la référence pour calculer la largeur
    inputRef.current = event.currentTarget;
  };

  // Fermer le menu déroulant personnalisé
  const handleCloseMenu = (event) => {
    // Empêcher la propagation pour éviter la fermeture
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  // Sélectionner une option dans le menu déroulant
  const handleSelectItem = (value) => (event) => {
    // Empêcher la propagation pour éviter la fermeture
    event.stopPropagation();
    updateFormData('occasion', value);
    handleCloseMenu();
  };

  // Handle input change pour le champ de texte
  const handleChange = (field) => (event) => {
    // Empêcher la propagation pour éviter la fermeture du modal
    event.stopPropagation();
    updateFormData(field, event.target.value);
  };

  // Gestionnaire spécifique pour empêcher la propagation sur tous les événements importants
  const handleInputClick = (event) => {
    event.stopPropagation();
  };

  // Obtenir le texte à afficher dans le sélecteur
  const getOccasionDisplayText = () => {
    if (!formData.occasion) {
      return 'Aucune occasion particulière';
    }
    return formData.occasion;
  };

  return (
    <Fade in timeout={400}>
      <Box onClick={(e) => e.stopPropagation()}>
        <DetailsContainer>
          <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
            Détails supplémentaires
          </Typography>

          {/* Menu déroulant personnalisé avec Popover */}
          <NoEventPropagationBox onClick={(e) => e.stopPropagation()}>
            <FormControl fullWidth variant="outlined" sx={{mb: 3}} onClick={(e) => e.stopPropagation()}>
              <InputLabel htmlFor="occasion-select">Occasion spéciale</InputLabel>
              <CustomSelect
                id="occasion-select"
                label="Occasion spéciale"
                value={getOccasionDisplayText()}
                onClick={handleOpenMenu}
                ref={inputRef}
                readOnly
                endAdornment={
                  <InputAdornment position="end">
                    <ArrowDropDownIcon />
                  </InputAdornment>
                }
                startAdornment={
                  <InputAdornment position="start">
                    <EmojiEventsIcon fontSize="small" color="primary" />
                  </InputAdornment>
                }
              />

              <CustomPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleCloseMenu}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  onClick: (e) => e.stopPropagation(),
                  style: {
                    width: inputRef.current ? inputRef.current.clientWidth : undefined,
                  },
                }}
              >
                <List sx={{width: '100%', padding: 0}}>
                  {OCCASIONS.map((option, index) => (
                    <React.Fragment key={index}>
                      <CustomListItem
                        isSelected={formData.occasion === option}
                        onClick={handleSelectItem(option)}
                      >
                        <ListItemText
                          primary={option || 'Aucune occasion particulière'}
                        />
                      </CustomListItem>
                      {index < OCCASIONS.length - 1 && (
                        <Divider component="li" />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </CustomPopover>
            </FormControl>
          </NoEventPropagationBox>

          {/* Champ de texte des demandes spéciales */}
          <NoEventPropagationBox onClick={(e) => e.stopPropagation()}>
            <Paper
              elevation={0}
              onClick={(e) => e.stopPropagation()}
              sx={{mb: 3, position: 'relative'}}
            >
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="special-requests">Demandes spéciales</InputLabel>
                <SpecialRequestsInput
                  id="special-requests"
                  label="Demandes spéciales"
                  multiline
                  rows={4}
                  value={formData.specialRequests || ''}
                  onChange={handleChange('specialRequests')}
                  onClick={handleInputClick}
                  onFocus={handleInputClick}
                  onMouseDown={handleInputClick}
                  onMouseUp={handleInputClick}
                  placeholder="Allergies, requêtes particulières, préférences de placement..."
                  startAdornment={
                    <InputAdornment position="start" sx={{alignSelf: 'flex-start', mt: 1.5}}>
                      <CommentIcon fontSize="small" color="primary" />
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Paper>
          </NoEventPropagationBox>

          {errors.api && (
            <Alert severity="error" sx={{mt: 2, p: 2}} variant="filled">
              {errors.api}
            </Alert>
          )}
        </DetailsContainer>
      </Box>
    </Fade>
  );
}

AdditionalDetailsStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func, // Add this to PropTypes
};
